// Service Google Photos pour sauvegarder les preuves
import googleAuthService from './googleAuth';

const PHOTOS_API_BASE = 'https://photoslibrary.googleapis.com/v1';
const ALBUM_TITLE = 'ADHD Planner - Preuves';

class GooglePhotosService {
  constructor() {
    this.albumId = null;
    this.isInitialized = false;
  }

  // Vérifier si le service est prêt
  isReady() {
    return googleAuthService.checkSignedIn();
  }

  // Initialiser l'album de l'application
  async initAlbum() {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    try {
      // Chercher l'album existant
      const albums = await this.listAlbums();
      const existingAlbum = albums.find(a => a.title === ALBUM_TITLE);

      if (existingAlbum) {
        this.albumId = existingAlbum.id;
        console.log('Album Google Photos trouvé:', this.albumId);
      } else {
        // Créer l'album
        this.albumId = await this.createAlbum(ALBUM_TITLE);
        console.log('Album Google Photos créé:', this.albumId);
      }

      this.isInitialized = true;
      return this.albumId;
    } catch (error) {
      // 403 = API non activée dans Google Cloud Console (normal, service optionnel)
      if (error.message?.includes('403')) {
        console.info('📷 Google Photos: API non activée (optionnel)');
      } else {
        console.warn('Google Photos non disponible:', error.message);
      }
      this.isInitialized = false;
      return null;
    }
  }

  // Lister les albums
  async listAlbums() {
    const accessToken = googleAuthService.getAccessToken();

    const response = await fetch(`${PHOTOS_API_BASE}/albums?pageSize=50`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur API Photos: ${response.status}`);
    }

    const data = await response.json();
    return data.albums || [];
  }

  // Créer un album
  async createAlbum(title) {
    const accessToken = googleAuthService.getAccessToken();

    const response = await fetch(`${PHOTOS_API_BASE}/albums`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        album: { title },
      }),
    });

    if (!response.ok) {
      throw new Error(`Erreur création album: ${response.status}`);
    }

    const data = await response.json();
    return data.id;
  }

  // Uploader une image
  async uploadImage(imageData, description = '') {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    if (!this.isInitialized) {
      await this.initAlbum();
    }

    if (!this.albumId) {
      console.warn('Album Google Photos non disponible');
      return null;
    }

    try {
      const accessToken = googleAuthService.getAccessToken();

      // Étape 1: Upload le fichier binaire
      // Convertir base64 en blob si nécessaire
      let blob;
      if (typeof imageData === 'string' && imageData.startsWith('data:')) {
        const response = await fetch(imageData);
        blob = await response.blob();
      } else {
        blob = imageData;
      }

      const uploadResponse = await fetch(`${PHOTOS_API_BASE}/uploads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/octet-stream',
          'X-Goog-Upload-Content-Type': blob.type || 'image/jpeg',
          'X-Goog-Upload-Protocol': 'raw',
        },
        body: blob,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Erreur upload: ${uploadResponse.status}`);
      }

      const uploadToken = await uploadResponse.text();

      // Étape 2: Créer l'élément média dans l'album
      const createResponse = await fetch(`${PHOTOS_API_BASE}/mediaItems:batchCreate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          albumId: this.albumId,
          newMediaItems: [
            {
              description: description,
              simpleMediaItem: {
                uploadToken: uploadToken,
                fileName: `proof_${Date.now()}.jpg`,
              },
            },
          ],
        }),
      });

      if (!createResponse.ok) {
        throw new Error(`Erreur création média: ${createResponse.status}`);
      }

      const createData = await createResponse.json();
      const mediaItem = createData.newMediaItemResults?.[0]?.mediaItem;

      if (mediaItem) {
        console.log('Photo uploadée sur Google Photos:', mediaItem.id);
        return {
          id: mediaItem.id,
          productUrl: mediaItem.productUrl,
          baseUrl: mediaItem.baseUrl,
        };
      }

      return null;
    } catch (error) {
      console.error('Erreur upload Google Photos:', error);
      // On ne throw pas l'erreur pour ne pas bloquer l'app
      return null;
    }
  }

  // Récupérer les photos de l'album
  async getAlbumPhotos(pageSize = 50) {
    if (!this.isReady() || !this.albumId) {
      return [];
    }

    try {
      const accessToken = googleAuthService.getAccessToken();

      const response = await fetch(`${PHOTOS_API_BASE}/mediaItems:search`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          albumId: this.albumId,
          pageSize: pageSize,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur récupération photos: ${response.status}`);
      }

      const data = await response.json();
      return data.mediaItems || [];
    } catch (error) {
      console.error('Erreur récupération photos:', error);
      return [];
    }
  }

  // Obtenir l'URL d'une photo avec dimensions
  getPhotoUrl(baseUrl, width = 400, height = 400) {
    if (!baseUrl) return null;
    return `${baseUrl}=w${width}-h${height}`;
  }

  // Obtenir le statut du service
  getStatus() {
    return {
      isReady: this.isReady(),
      isInitialized: this.isInitialized,
      albumId: this.albumId,
    };
  }
}

// Singleton
const googlePhotosService = new GooglePhotosService();
export default googlePhotosService;
